<script setup lang="ts">
import type { CharacterAchievementItem } from '~/stores/achievements'
import type { TextSettings, ThemeSettings } from '~/stores/auth'

const props = defineProps<{
  achievements: CharacterAchievementItem[]
  textSettings: TextSettings
  themeSettings: ThemeSettings
  editMode?: boolean
}>()

const emit = defineEmits<{
  'open-achievements-manager': []
}>()

const displayedAchievements = computed(() => {
  return [...props.achievements]
    .filter(b => b.is_displayed)
    .sort((a, b) => a.display_position - b.display_position)
})

const iconStyle = computed(() => props.themeSettings.iconStyle || 'default')
const iconSize = computed(() => props.themeSettings.iconSize || 40)
const achievementIconSize = computed(() => Math.round(iconSize.value * 0.35))

const containerAnimClass = computed(() => {
  const map: Record<string, string> = {
    bounce: 'animate-icon-bounce',
    pulse: 'animate-icon-pulse',
    wave: 'animate-icon-wave',
    shake: 'animate-icon-shake',
    flicker: 'animate-icon-flicker',
    neon: 'animate-icon-neon'
  }
  return map[iconStyle.value] || ''
})

const iconAnimClass = computed(() => {
  const map: Record<string, string> = {
    spin: 'animate-icon-spin',
    rainbow: 'animate-icon-rainbow',
    glitch: 'animate-icon-glitch'
  }
  return map[iconStyle.value] || ''
})

function getContainerStyle(achievement: CharacterAchievementItem, index: number) {
  const primaryColor = props.themeSettings.primaryColor
  const achievementColor = achievement.achievement.icon_color || '#FFFFFF'
  const style: Record<string, string> = {}

  if (iconStyle.value === 'bordered') {
    style.borderWidth = '1.5px'
    style.borderColor = `color-mix(in srgb, ${primaryColor} 40%, transparent)`
  }

  if (iconStyle.value === 'glow') {
    style.filter = `drop-shadow(0 0 4px color-mix(in srgb, ${achievementColor} 40%, transparent)) drop-shadow(0 0 10px color-mix(in srgb, ${achievementColor} 20%, transparent))`
  }

  if (iconStyle.value === 'neon') {
    style.filter = `drop-shadow(0 0 3px ${achievementColor}) drop-shadow(0 0 8px ${achievementColor}) drop-shadow(0 0 18px ${achievementColor}) drop-shadow(0 0 36px ${achievementColor})`
  }

  if (iconStyle.value === 'wave') {
    style.animationDelay = `${index * 0.15}s`
  }

  return style
}

function getIconStyle(achievement: CharacterAchievementItem) {
  const achievementColor = achievement.achievement.icon_color || '#FFFFFF'
  return {
    color: achievementColor,
    width: `${achievementIconSize.value}px`,
    height: `${achievementIconSize.value}px`
  }
}

const PLAN_ACHIEVEMENT_IDS = ['pro', 'elite']

function isPlanAchievement(item: CharacterAchievementItem) {
  return PLAN_ACHIEVEMENT_IDS.includes(item.achievement_id)
}

function getDonationLabel(item: CharacterAchievementItem): string | null {
  if (!item.metadata?.amount_cents) return null
  const symbol = (item.metadata.currency || 'eur') === 'usd' ? '$' : '€'
  const count = item.metadata.donation_count ?? 0

  // Custom donation: show cumulative total
  if (item.achievement.unlock_config?.custom && item.metadata.total_donated_cents) {
    const total = (item.metadata.total_donated_cents / 100).toFixed(item.metadata.total_donated_cents % 100 === 0 ? 0 : 2)
    return `${symbol}${total} total`
  }

  // Fixed donation with multiplier
  if (count > 1) {
    return `x${count}`
  }

  // Single donation: show amount
  const amount = (item.metadata.amount_cents / 100).toFixed(item.metadata.amount_cents % 100 === 0 ? 0 : 2)
  return `${symbol}${amount}`
}

function formatGrantedDate(dateStr: string) {
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-US', { month: 'short', year: 'numeric' })
}

const achievementHoverBg = computed(() => {
  return `color-mix(in srgb, ${props.themeSettings.primaryColor} 10%, transparent)`
})

function getTooltipText(item: CharacterAchievementItem): string {
  let text = item.achievement.name
  if (isPlanAchievement(item) && item.granted_at) {
    text += ` since ${formatGrantedDate(item.granted_at)}`
  } else {
    const label = getDonationLabel(item)
    if (label) text += ` · ${label}`
  }
  return text
}
</script>

<template>
  <div
    v-if="displayedAchievements.length > 0 || editMode"
    class="flex items-center gap-0.5 flex-wrap"
  >
    <UTooltip
      v-for="(item, index) in displayedAchievements"
      :key="item.id"
      :text="getTooltipText(item)"
      :delay-duration="300"
    >
      <div
        class="rounded-full flex items-center justify-center px-1.5 py-1 transition-all duration-200"
        :class="[
          iconStyle === 'bordered' ? 'border' : '',
          containerAnimClass
        ]"
        :style="{
          backgroundColor: achievementHoverBg,
          ...getContainerStyle(item, index)
        }"
      >
        <div
          class="flex items-center justify-center shrink-0"
          :style="{ width: `${achievementIconSize}px`, height: `${achievementIconSize}px` }"
        >
          <UIcon
            :name="item.achievement.icon || 'i-lucide-award'"
            :class="['transition-all duration-200', iconAnimClass]"
            :style="getIconStyle(item)"
          />
        </div>
      </div>
    </UTooltip>

    <!-- Manage achievements button (edit mode only) -->
    <UTooltip
      v-if="editMode"
      text="Manage achievements"
      :delay-duration="300"
    >
      <button
        class="size-7 ml-1 rounded-full border-2 border-dashed flex items-center justify-center transition-all duration-200 cursor-pointer hover:scale-105 group"
        :style="{
          borderColor: 'rgba(255, 255, 255, 0.12)',
          color: 'rgba(255, 255, 255, 0.3)'
        }"
        @click="emit('open-achievements-manager')"
      >
        <UIcon
          name="i-lucide-settings"
          class="size-3 group-hover:text-white/50 transition-colors"
        />
      </button>
    </UTooltip>
  </div>
</template>

<style scoped>
/* Bounce */
.animate-icon-bounce {
  animation: icon-bounce 1.8s ease-in-out infinite;
}
@keyframes icon-bounce {
  0%, 100% { transform: translateY(0); }
  40% { transform: translateY(-5px); }
  60% { transform: translateY(-2px); }
}

/* Pulse - breathing scale */
.animate-icon-pulse {
  animation: icon-pulse 2s ease-in-out infinite;
}
@keyframes icon-pulse {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.15); }
}

/* Wave - staggered float (delay set via inline style) */
.animate-icon-wave {
  animation: icon-wave 2.5s ease-in-out infinite;
}
@keyframes icon-wave {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(-6px); }
}

/* Neon - subtle brightness pulse paired with strong drop-shadow */
.animate-icon-neon {
  animation: icon-neon 2s ease-in-out infinite;
}
@keyframes icon-neon {
  0%, 100% { opacity: 1; }
  50% { opacity: 0.8; }
}

/* Shake - rapid vibration */
.animate-icon-shake {
  animation: icon-shake 0.4s ease-in-out infinite;
}
@keyframes icon-shake {
  0%, 100% { transform: translateX(0); }
  25% { transform: translateX(-2px); }
  75% { transform: translateX(2px); }
}

/* Flicker - neon sign stutter */
.animate-icon-flicker {
  animation: icon-flicker 3s steps(1) infinite;
}
@keyframes icon-flicker {
  0%, 100% { opacity: 1; }
  5% { opacity: 0.3; }
  10% { opacity: 1; }
  15% { opacity: 0.6; }
  20% { opacity: 1; }
  55% { opacity: 1; }
  57% { opacity: 0.2; }
  60% { opacity: 1; }
  80% { opacity: 1; }
  82% { opacity: 0.4; }
  85% { opacity: 1; }
}

/* Spin - slow rotation (icon only) */
.animate-icon-spin {
  animation: icon-spin 3s linear infinite;
}
@keyframes icon-spin {
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
}

/* Rainbow - hue cycling (icon only) */
.animate-icon-rainbow {
  animation: icon-rainbow 3s linear infinite;
}
@keyframes icon-rainbow {
  from { filter: hue-rotate(0deg); }
  to { filter: hue-rotate(360deg); }
}

/* Glitch - RGB shift + jitter (icon only) */
.animate-icon-glitch {
  animation: icon-glitch 2s steps(1) infinite;
}
@keyframes icon-glitch {
  0%, 30%, 60%, 100% { transform: translate(0); filter: none; }
  10% { transform: translate(-2px, 1px); filter: hue-rotate(90deg); }
  20% { transform: translate(2px, -1px); filter: hue-rotate(200deg); }
  40% { transform: translate(1px, 2px); filter: hue-rotate(270deg); }
  50% { transform: translate(-1px, -2px); filter: hue-rotate(45deg); }
}
</style>
